import { Seo } from "@/components/Seo";
import { ModuleTrialPrompt } from "@/components/ModuleTrialPrompt";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  FileText, Loader2, UploadCloud, Sparkles, X, Send, Scale, FileSignature,
  Gavel, ListChecks, RotateCcw, Paperclip,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Disclaimer } from "@/components/Disclaimer";
import { Markdown } from "@/components/Markdown";
import { VoiceButton } from "@/components/VoiceButton";
import { ChatHistory } from "@/components/ChatHistory";
import { LimitModal } from "@/components/LimitModal";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LANGS = [
  { code: "en", label: "English" }, { code: "hi", label: "हिन्दी · Hindi" }, { code: "gu", label: "ગુજરાતી · Gujarati" },
  { code: "mr", label: "मराठी · Marathi" }, { code: "ta", label: "தமிழ் · Tamil" }, { code: "te", label: "తెలుగు · Telugu" },
  { code: "bn", label: "বাংলা · Bengali" }, { code: "kn", label: "ಕನ್ನಡ · Kannada" }, { code: "pa", label: "ਪੰਜਾਬੀ · Punjabi" },
  { code: "ml", label: "മലയാളം · Malayalam" }, { code: "ur", label: "اردو · Urdu" },
];

const QUICK_ACTIONS = [
  { icon: FileSignature, label: "Brief for Complainant", prompt: "Prepare a complete case brief and the strongest arguments in favour of the complainant / petitioner / prosecution, grounded in this document and Indian law." },
  { icon: Scale, label: "Brief for Respondent", prompt: "Prepare a complete case brief and the strongest arguments in favour of the respondent / accused / defence, grounded in this document and Indian law." },
  { icon: Gavel, label: "Generate Arguments", prompt: "Generate the key legal arguments arising from this document for both sides, with the supporting statutes and precedents." },
  { icon: ListChecks, label: "Key Issues & Ratio", prompt: "Identify the key legal issues, the ratio decidendi, the holding, and the most important paragraphs in this document." },
];

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024; // 100 MB

export default function CaseBrief() {
  const [phase, setPhase] = useState("upload"); // upload | processing | chat
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("judgment");
  const [language, setLanguage] = useState("en");
  const [uploading, setUploading] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [usage, setUsage] = useState(null);
  const [limitModal, setLimitModal] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const pollRef = useRef(null);
  const { user } = useAuth();

  const loadUsage = useCallback(() => {
    api.get("/casebrief/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);
  useEffect(() => { loadUsage(); }, [loadUsage]);
  useEffect(() => { if (messages.length === 0) return; endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const startSession = async () => {
    if (!file) { toast.error("Upload a judgment or case file (PDF / DOCX / TXT / image)."); return; }
    if (file.size > MAX_UPLOAD_BYTES) { toast.error("File too large. The maximum file size is 100 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_kind", kind);
      fd.append("language", language);
      const { data } = await api.post("/casebrief/upload", fd);
      setSession(data);
      if (data.status === "processing") {
        setProgress({ done: 0, total: data.pages || 0 });
        setPhase("processing");
        setHistoryKey((k) => k + 1);
        pollStatus(data.session_id);
        return;
      }
      enterChat(data);
    } catch (e) {
      if (e.response?.status === 402) { showLimit(e); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const showLimit = (e) => {
    const detail = e.response?.data?.detail || {};
    setLimitModal(detail.reason === "guest_limit"
      ? { mode: "guest" }
      : { mode: "free", message: detail.message });
  };

  const enterChat = (data) => {
    setSession(data);
    setMessages([{
      role: "assistant",
      content: `**${data.doc_name}** is loaded. I've read your ${data.doc_kind === "casefile" ? "case file" : "judgment"}. Ask me anything — or tap a quick action below to generate a brief, build arguments for either side, or pull out the key issues & ratio.`,
      intro: true,
    }]);
    setPhase("chat");
    loadUsage();
    setHistoryKey((k) => k + 1);
  };

  const pollStatus = (sid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/casebrief/${sid}/status`);
        setProgress(data.progress || null);
        if (data.status === "ready") {
          clearInterval(pollRef.current);
          enterChat({ session_id: sid, doc_name: data.doc_name, doc_kind: data.doc_kind });
        } else if (data.status === "failed") {
          clearInterval(pollRef.current);
          toast.error(data.error || "We couldn't read this document.");
          reset();
        }
      } catch {
        // keep polling; transient errors are ignored
      }
    }, 3000);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending || !session) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setSending(true);
    try {
      const { data } = await api.post("/casebrief/chat", { session_id: session.session_id, message: content, language });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setHistoryKey((k) => k + 1);
    } catch (e) {
      if (e.response?.status === 402) {
        setMessages((m) => m.slice(0, -1)); // remove the unsent user bubble
        setInput(content);                  // restore the typed prompt
        showLimit(e);
        return;
      }
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", content: msg, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setPhase("upload"); setSession(null); setMessages([]); setFile(null); setInput(""); setProgress(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const loadSession = async (sid) => {
    try {
      const { data } = await api.get(`/casebrief/${sid}/messages`);
      const sess = data.session;
      const status = sess.status || "ready";
      setKind(sess.doc_kind || "judgment");
      setLanguage(sess.language || "en");
      if (status === "processing") {
        setSession({ session_id: sess.id, doc_name: sess.doc_name, doc_kind: sess.doc_kind });
        setProgress(sess.progress || null);
        setPhase("processing");
        pollStatus(sess.id);
        setLimitModal(null);
        return;
      }
      if (status === "failed") {
        toast.error(sess.error || "This document could not be processed.");
        return;
      }
      setSession({ session_id: sess.id, doc_name: sess.doc_name, doc_kind: sess.doc_kind });
      setMessages(
        data.messages.length
          ? data.messages.map((m) => ({ role: m.role, content: m.content }))
          : [{ role: "assistant", content: `**${sess.doc_name}** is loaded. Ask me anything about this ${sess.doc_kind === "casefile" ? "case file" : "judgment"}.`, intro: true }]
      );
      setPhase("chat");
      setLimitModal(null);
    } catch {
      toast.error("Could not load that conversation.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12" data-testid="casebrief-page">
      <ModuleTrialPrompt />
      <Seo title="CaseBrief AI — Summarise Judgments & Case Files | JusticeVerse" description="Upload any judgment or case file — even scanned/image PDFs — and get an instant case brief with facts, issues, ratio and citations, then chat with the document. JusticeVerse CaseBrief AI." />
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2">
          <FileText className="w-4 h-4" /> Module 03
        </span>
        <ChatHistory
          user={user}
          listUrl="/casebrief/sessions"
          normalize={(d) => d.map((s) => ({ id: s.id, title: s.doc_name, created_at: s.created_at }))}
          onSelect={loadSession}
          onNew={reset}
          deleteUrl={(id) => `/casebrief/${id}`}
          deleteAllUrl="/casebrief/sessions"
          activeId={session?.session_id}
          reloadKey={historyKey}
          label="CaseBrief History"
        />
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-3">CaseBrief AI</h1>
      <p className="text-inkmuted mt-4 max-w-2xl">
        Upload a <span className="text-ink">judgment</span> or <span className="text-ink">your own case file</span> (PDF / DOCX / TXT), then <span className="gold-text">chat</span> — generate briefs from either side, build arguments and ask follow-ups. CaseBrief remembers your conversation.
      </p>

      {usage && usage.access && (
        <div className="mt-5 inline-flex items-center gap-2 glass-card rounded-full px-4 py-2" data-testid="casebrief-usage-badge">
          <Sparkles className="w-3.5 h-3.5 text-gold" />
          {usage.unlimited
            ? <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-inkmuted">Unlimited CaseBriefs</span>
            : <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-inkmuted"><span className="text-ink">{usage.remaining}</span> of {usage.quota} CaseBriefs left this cycle</span>}
        </div>
      )}

      {/* Upload phase */}
      {phase === "upload" && (
        <div className="glass-card rounded-3xl p-7 mt-8 space-y-5" data-testid="casebrief-upload">
          <div className="flex gap-2" data-testid="doc-kind-toggle">
            {[["judgment", "Court Judgment"], ["casefile", "Your Case File"]].map(([k, label]) => (
              <button key={k} onClick={() => setKind(k)} data-testid={`kind-${k}`}
                className={`flex-1 rounded-full px-4 py-2.5 text-xs font-mono uppercase tracking-[0.12em] border transition-all ${kind === k ? "bg-btn text-btnink border-ink" : "border-hairline text-inkmuted hover:text-ink"}`}>
                {label}
              </button>
            ))}
          </div>

          <div onClick={() => fileRef.current?.click()}
            className="border border-dashed border-hairline rounded-2xl p-10 text-center cursor-pointer hover:border-gold/40 transition-all"
            data-testid="upload-dropzone">
            <UploadCloud className="w-8 h-8 text-gold mx-auto" />
            {file ? (
              <div className="mt-3 flex items-center justify-center gap-2 text-sm text-ink">
                <Paperclip className="w-4 h-4 text-gold" /> {file.name}
                <button onClick={(e) => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-inkfaint hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <p className="text-sm text-inkmuted mt-3">Upload your document — <span className="text-ink">PDF, DOCX, TXT or image</span><br /><span className="text-inkfaint text-xs">Scanned / photographed (image-only) PDFs are read automatically. Max file size 100 MB · up to 2000 pages.</span></p>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,image/*,.png,.jpg,.jpeg,.webp,.heic,.heif,.tiff,.bmp" className="hidden" data-testid="file-input"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                if (f && f.size > MAX_UPLOAD_BYTES) { toast.error("File too large. The maximum file size is 100 MB."); e.target.value = ""; return; }
                setFile(f);
              }} />
          </div>

          <div className="flex items-center gap-3">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[180px] bg-surface border-hairline rounded-xl text-sm text-ink" data-testid="brief-lang-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-panel border-hairline text-ink shadow-xl max-h-72">
                {LANGS.map((l) => <SelectItem key={l.code} value={l.code} className="text-sm focus:bg-gold/15 focus:text-ink data-[state=checked]:text-gold">{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={startSession} disabled={uploading || !file} data-testid="start-casebrief-btn"
              className="flex-1 rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-50 px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-2">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {uploading ? "Reading & scanning document…" : "Start CaseBrief"}
            </button>
          </div>
          {user && usage?.access && !usage.unlimited && (
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.15em] text-inkfaint" data-testid="generate-usage-hint">
              {usage.remaining} of {usage.quota} CaseBriefs left · 1 credit per document, then unlimited chat
            </p>
          )}
        </div>
      )}

      {/* Processing (background OCR of large scanned PDFs) */}
      {phase === "processing" && (
        <div className="glass-card rounded-3xl p-10 mt-8 text-center" data-testid="casebrief-processing">
          <Loader2 className="w-10 h-10 text-gold mx-auto animate-spin" />
          <h3 className="font-serif text-2xl text-ink mt-6">Reading your scanned document…</h3>
          <p className="text-sm text-inkmuted mt-3 max-w-md mx-auto">
            {session?.doc_name ? <span className="text-ink">{session.doc_name}</span> : "Your document"} appears to be a scanned / image PDF, so I am reading every page with OCR. Large documents can take a few minutes — the chat will open automatically when it is ready.
          </p>
          {progress?.total > 0 && (
            <div className="mt-7 max-w-sm mx-auto" data-testid="ocr-progress">
              <div className="h-2 rounded-full bg-surface2 overflow-hidden">
                <div className="h-full bg-gold transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%` }} />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-inkfaint mt-3">
                {progress.done} / {progress.total} pages read
              </p>
            </div>
          )}
          <button onClick={reset} data-testid="cancel-processing-btn"
            className="mt-8 text-xs font-mono uppercase tracking-wider text-inkfaint hover:text-ink transition-colors">
            Upload a different document
          </button>
        </div>
      )}

      {/* Chat phase */}
      {phase === "chat" && session && (
        <div className="mt-8 space-y-4">
          <div className="flex items-center justify-between glass-card rounded-2xl px-5 py-3" data-testid="casebrief-doc-bar">
            <span className="flex items-center gap-2 text-sm text-inkmuted min-w-0">
              <Paperclip className="w-4 h-4 text-gold shrink-0" />
              <span className="truncate">{session.doc_name}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-inkfaint shrink-0">· {session.doc_kind === "casefile" ? "Case File" : "Judgment"}</span>
            </span>
            <button onClick={reset} data-testid="new-document-btn" className="text-xs font-mono uppercase tracking-wider text-inkmuted hover:text-ink flex items-center gap-1.5 shrink-0">
              <RotateCcw className="w-3.5 h-3.5" /> New
            </button>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="quick-actions">
            {QUICK_ACTIONS.map((a) => (
              <button key={a.label} onClick={() => send(a.prompt)} disabled={sending}
                data-testid={`quick-${a.label.replace(/\s+/g, "-").toLowerCase()}`}
                className="flex items-center gap-2 text-xs text-ink border border-hairline rounded-full px-4 py-2 hover:border-gold/40 hover:text-ink transition-all disabled:opacity-40">
                <a.icon className="w-3.5 h-3.5 text-gold" /> {a.label}
              </button>
            ))}
          </div>

          <div className="glass-card rounded-3xl flex flex-col overflow-hidden" data-testid="casebrief-chat">
            <div className="flex-1 overflow-y-auto p-6 space-y-5 min-h-[42vh] max-h-[60vh]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-5 py-4 text-sm leading-relaxed ${m.role === "assistant" && !m.error ? "" : "whitespace-pre-wrap"} ${
                    m.role === "user" ? "bg-surface2 border border-hairline text-ink"
                      : m.error ? "bg-red-500/10 border border-red-500/30 text-red-300"
                      : "bg-surface border border-gold/20 text-ink"}`}>
                    {m.role === "assistant" && !m.error ? <Markdown>{m.content}</Markdown> : m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-surface border border-gold/20 rounded-2xl px-5 py-4 flex items-center gap-2 text-inkmuted text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Analysing the document…
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="p-4 border-t border-hairline">
              <div className="flex items-end gap-2 bg-surface border border-hairline rounded-2xl px-3">
                <VoiceButton lang={language} onTranscript={(t) => setInput((p) => (p ? p + " " : "") + t)} className="rounded-full w-11 h-11 flex items-center justify-center hover:bg-surface transition-all shrink-0" />
                <textarea value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  data-testid="casebrief-input" rows={1}
                  placeholder="e.g. Draft a brief in favour of the complainant…"
                  className="flex-1 bg-transparent resize-none outline-none text-ink placeholder:text-inkfaint text-sm py-3 px-2 max-h-32" />
                <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="casebrief-send-btn"
                  className="rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-40 w-11 h-11 flex items-center justify-center transition-all shrink-0 mb-2">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Disclaimer className="mt-6 justify-center" />

      <LimitModal mode={limitModal?.mode} freeMessage={limitModal?.message} onClose={() => setLimitModal(null)} />
    </div>
  );
}
